export type AiProvider = 'openai' | 'anthropic' | 'gemini' | 'xai'

export type AiCandidate = {
  handle: string
  displayName: string
  bio: string
}

export type GenerateOptions = {
  provider: AiProvider
  apiKey: string
  /** What kind of personas to generate. Free-form. */
  prompt: string
  count: number
  /** Optional model override per provider */
  model?: string
}

const DEFAULT_MODEL: Record<AiProvider, string> = {
  openai: 'gpt-4o-mini',
  anthropic: 'claude-haiku-4-5-20251001',
  gemini: 'gemini-1.5-flash',
  xai: 'grok-2-latest',
}

const SYSTEM_PROMPT = `You generate Bluesky personas. Return STRICT JSON, no prose.
Schema: {"candidates":[{"handle":"string","displayName":"string","bio":"string"}]}
Rules:
- handle: lowercase, only [a-z0-9-], 4-20 chars, no leading/trailing dash, must be plausible.
- displayName: 1-50 chars.
- bio: 1-160 chars.
- Generate exactly the requested count.
- Avoid impersonation, hate, or anything resembling a known public figure.`

function tryParseJSON<T>(text: string): T | null {
  // Try whole text, then look for the first {...} block
  try {
    return JSON.parse(text) as T
  } catch {
    /* fall through */
  }
  const m = text.match(/\{[\s\S]*\}/)
  if (!m) return null
  try {
    return JSON.parse(m[0]) as T
  } catch {
    return null
  }
}

async function callOpenAi(opts: GenerateOptions, baseUrl: string, model: string): Promise<string> {
  const r = await fetch(`${baseUrl}/v1/chat/completions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${opts.apiKey}`,
    },
    body: JSON.stringify({
      model,
      response_format: { type: 'json_object' },
      messages: [
        { role: 'system', content: SYSTEM_PROMPT },
        {
          role: 'user',
          content: `Generate ${opts.count} personas. Theme: ${opts.prompt}`,
        },
      ],
    }),
  })
  if (!r.ok) throw new Error(`${r.status} ${await r.text()}`)
  const data = (await r.json()) as { choices?: Array<{ message?: { content?: string } }> }
  return data.choices?.[0]?.message?.content ?? ''
}

async function callAnthropic(opts: GenerateOptions, model: string): Promise<string> {
  const r = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': opts.apiKey,
      'anthropic-version': '2023-06-01',
      // Anthropic blocks browser calls without this opt-in; the key is exposed
      // to the user, which is a tradeoff they accept by entering it client-side.
      'anthropic-dangerous-direct-browser-access': 'true',
    },
    body: JSON.stringify({
      model,
      max_tokens: 4096,
      system: SYSTEM_PROMPT,
      messages: [
        {
          role: 'user',
          content: `Generate ${opts.count} personas. Theme: ${opts.prompt}`,
        },
      ],
    }),
  })
  if (!r.ok) throw new Error(`${r.status} ${await r.text()}`)
  const data = (await r.json()) as { content?: Array<{ type?: string; text?: string }> }
  const block = (data.content ?? []).find((b) => b.type === 'text')
  return block?.text ?? ''
}

async function callGemini(opts: GenerateOptions, model: string): Promise<string> {
  const url = `https://generativelanguage.googleapis.com/v1beta/models/${encodeURIComponent(
    model,
  )}:generateContent?key=${encodeURIComponent(opts.apiKey)}`
  const r = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      systemInstruction: { parts: [{ text: SYSTEM_PROMPT }] },
      contents: [
        {
          role: 'user',
          parts: [{ text: `Generate ${opts.count} personas. Theme: ${opts.prompt}` }],
        },
      ],
      generationConfig: { responseMimeType: 'application/json' },
    }),
  })
  if (!r.ok) throw new Error(`${r.status} ${await r.text()}`)
  const data = (await r.json()) as {
    candidates?: Array<{ content?: { parts?: Array<{ text?: string }> } }>
  }
  return data.candidates?.[0]?.content?.parts?.[0]?.text ?? ''
}

export async function generateCandidates(opts: GenerateOptions): Promise<AiCandidate[]> {
  if (!opts.apiKey) throw new Error('API key required')
  if (opts.count <= 0) return []
  const model = opts.model ?? DEFAULT_MODEL[opts.provider]
  let raw = ''
  switch (opts.provider) {
    case 'openai':
      raw = await callOpenAi(opts, 'https://api.openai.com', model)
      break
    case 'anthropic':
      raw = await callAnthropic(opts, model)
      break
    case 'gemini':
      raw = await callGemini(opts, model)
      break
    case 'xai':
      raw = await callOpenAi(opts, 'https://api.x.ai', model)
      break
  }
  const parsed = tryParseJSON<{ candidates?: AiCandidate[] }>(raw)
  if (!parsed?.candidates) {
    throw new Error('AI returned an unparseable response')
  }
  return parsed.candidates.slice(0, opts.count).map((c) => ({
    handle: String(c.handle ?? '').toLowerCase().replace(/[^a-z0-9-]/g, ''),
    displayName: String(c.displayName ?? ''),
    bio: String(c.bio ?? ''),
  }))
}
